import {
  ApiError,
  type ChatMessage,
  chatMessages,
  chatSend,
} from '@cortex/web-api/clients/web/api';
import { Wordmark } from '@cortex/web-api/ui/web/Wordmark';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Composer } from '../components/Composer.js';
import { DemoDashboardLink } from '../components/DemoDashboardLink.js';
import { MessageBubble } from '../components/MessageBubble.js';
import { TypingIndicator } from '../components/TypingIndicator.js';

const POLL_BASE_MS = 1500;
const POLL_JITTER_MS = 1000;

interface Props {
  onLoggedOut: () => void;
}

export function ChatScreen({ onLoggedOut }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // True when the user just sent a message and we're waiting for the agent
  // reply to land. Heuristic: a message we sent is the most recent message,
  // and there's no later outbound. Cleared once a new outbound arrives.
  const [waitingForReply, setWaitingForReply] = useState(false);
  const [networkBlip, setNetworkBlip] = useState(false);
  const lastIdRef = useRef<string | undefined>(undefined);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Initial load + polling loop.
  const tick = useCallback(async () => {
    try {
      const r = await chatMessages(lastIdRef.current);
      setNetworkBlip(false);
      if (r.messages.length === 0) return;
      lastIdRef.current = r.messages[r.messages.length - 1]?.id ?? lastIdRef.current;
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        const merged = [...prev];
        for (const m of r.messages) if (!seen.has(m.id)) merged.push(m);
        return merged;
      });
      // If any new message was outbound, clear the waiting flag.
      if (r.messages.some((m) => m.direction === 'outbound')) {
        setWaitingForReply(false);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onLoggedOut();
        return;
      }
      // Network blip: don't drop the loop, just surface a calm note.
      setNetworkBlip(true);
    }
  }, [onLoggedOut]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const loop = async () => {
      if (cancelled) return;
      await tick();
      if (cancelled) return;
      const delay = POLL_BASE_MS + Math.floor(Math.random() * POLL_JITTER_MS);
      timer = setTimeout(loop, delay);
    };
    void loop();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [tick]);

  // Scroll to bottom whenever message count or waiting indicator changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll trigger uses count, not array identity
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, waitingForReply]);

  async function handleSend(body: string) {
    const optimistic: ChatMessage = {
      id: `optimistic-${Date.now()}`,
      direction: 'inbound',
      body,
      sentAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setWaitingForReply(true);
    try {
      const sent = await chatSend(body);
      // Replace the optimistic row with the server's truth so polling de-dupes.
      lastIdRef.current = sent.messageId;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === optimistic.id
            ? { ...optimistic, id: sent.messageId, sentAt: optimistic.sentAt }
            : m,
        ),
      );
    } catch (err) {
      // Mark failure inline; user can retry from the next composer submit.
      setMessages((prev) =>
        prev.map((m) =>
          m.id === optimistic.id ? { ...m, body: `${m.body}\n\n[didn't send — try again]` } : m,
        ),
      );
      setWaitingForReply(false);
      if (err instanceof ApiError && err.status === 401) onLoggedOut();
    }
  }

  return (
    <div className="app">
      <header className="app-header">
        <img src="/acel-cortex-mark.svg" alt="" width={28} height={28} />
        <Wordmark size={18} />
        <span style={{ marginLeft: 'auto' }} className="label">
          maintenance
        </span>
        <DemoDashboardLink />
        <a href="/site-auth/change-password" className="label">
          Change password
        </a>
      </header>
      <main className="app-main">
        <div
          ref={scrollRef}
          style={{
            flex: '1 1 auto',
            overflowY: 'auto',
            padding: 'var(--space-6)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
          }}
        >
          {messages.length === 0 && !waitingForReply && (
            <div
              style={{
                margin: 'auto',
                textAlign: 'center',
                color: 'var(--muted)',
                maxWidth: 360,
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-body)',
                lineHeight: 'var(--lh-body)',
              }}
            >
              Hi — I'm the maintenance assistant. Tell me what's going on and I'll get the right
              person on it.
            </div>
          )}
          {messages.map((m) => (
            <MessageBubble key={m.id} message={m} />
          ))}
          {waitingForReply && <TypingIndicator />}
          {networkBlip && (
            <div
              style={{
                alignSelf: 'center',
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--fs-mono-sm)',
                letterSpacing: 'var(--tracking-mono)',
                textTransform: 'uppercase',
                color: 'var(--muted)',
              }}
            >
              reconnecting…
            </div>
          )}
        </div>
        <Composer onSend={handleSend} />
      </main>
    </div>
  );
}
